import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "~/components/Loading";

interface DetailLayoutProps {
  children: ReactNode;
  loading?: boolean;
  error?: string | null;
  notFoundTitle?: string;
  backPath: string;
  backLabel: string;
  onRetry?: () => void;
}

export const DetailLayout: React.FC<DetailLayoutProps> = ({
  children,
  loading = false,
  error = null,
  notFoundTitle = "Content Not Found",
  backPath,
  backLabel,
  onRetry,
}) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[var(--background-primary)] transition-colors duration-300 animate-[fadeIn_0.3s_ease-in]">
        <div className="flex justify-center items-center min-h-[60vh] p-8">
          <Loading message="Loading content..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-[var(--background-primary)] transition-colors duration-300 animate-[fadeIn_0.3s_ease-in]">
        <div className="text-center py-16 px-8 max-w-[600px] mx-auto">
          <div className="text-6xl mb-4 opacity-70">⚠️</div>
          <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-4">
            {notFoundTitle}
          </h2>
          <p className="text-lg text-[var(--text-secondary)] mb-8 leading-relaxed">
            {error}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            {onRetry && (
              <button
                className="rounded-lg border-none px-6 py-3 text-base font-semibold cursor-pointer transition-all duration-200 inline-block bg-[var(--gradient-primary)] text-white hover:bg-[var(--gradient-secondary)] hover:-translate-y-0.5 hover:shadow-lg"
                onClick={onRetry}
              >
                Try Again
              </button>
            )}
            <button
              className="rounded-lg border-none px-6 py-3 text-base font-semibold cursor-pointer transition-all duration-200 inline-block bg-[var(--background-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--background-primary)] hover:border-primary hover:text-primary"
              onClick={() => navigate(backPath)}
            >
              {backLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[var(--background-primary)] transition-colors duration-300 animate-[fadeIn_0.3s_ease-in]">
      <div className="max-w-[1200px] mx-auto flex flex-col gap-5 pt-16 px-4 md:px-6 lg:px-8">
        <button
          className="flex w-fit mb-2 px-4 py-2 rounded-lg  hover:text-[var(--text-primary)]  transition-colors duration-200 font-medium"
          onClick={() => navigate(backPath)}
        >
          ← {backLabel}
        </button>
        {children}
      </div>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default DetailLayout;
